// A solution's license in the person's words (02-DESIGN/17-plans/
// 03-solution-licenses.md § install and upgrade with a license, § the
// record, the page and the end; decision 0070 § 9): a licensed catalogue
// entry *requires a license* with the publisher's hint; the three refusals
// of an install or an upgrade in one sentence each, with the hint; an
// installed licensed solution *licensed until 1 March 2027* with the
// builder's terms as given, and past its expiry *license expired*. The word
// on the page is **license**, pasted as a plan key is. The platform shows
// the terms and never reads them.

import { LicenseCode, type InstalledLicense } from "../../wire/solutions.ts";
import { dayOf } from "../settings/plan.ts";

/** REQUIRES is what a licensed catalogue entry says. */
export const REQUIRES = "requires a license";

/** hintSentence is the publisher's hint as a sentence: *Licenses at acme.example/licenses.* */
export function hintSentence(hint: string | undefined): string {
  const h = (hint ?? "").trim();
  if (h === "") {
    return "";
  }
  const s = h.charAt(0).toUpperCase() + h.slice(1);
  return /[.!?]$/.test(s) ? s : `${s}.`;
}

/** The check a `license-invalid` refusal names: *the signature check*, as the service writes it. */
const CHECK = /the ([a-z-]+) check/i;

/** CHECK_WORDS say which check a license failed, in the person's words (impire-sdk-go solution: LicenseCheck*). */
const CHECK_WORDS: Readonly<Record<string, string>> = {
  shape: "it is not a license",
  signature: "it is not signed by the solution's publisher",
  publisher: "it was issued by another publisher",
  install: "it is for another install",
  solution: "it is for another solution",
  workspace: "it is for another workspace",
};

/**
 * licenseRefusal is a refused install or update of a licensed solution in one
 * sentence with the hint, or nothing for any other refusal.
 */
export function licenseRefusal(err: unknown, display: string, hint: string | undefined): string | undefined {
  const code = typeof err === "object" && err !== null && "code" in err ? err.code : undefined;
  const message = typeof err === "object" && err !== null && "message" in err ? String(err.message) : "";
  const tail = hintSentence(hint);
  const join = (s: string) => (tail === "" ? s : `${s} ${tail}`);
  switch (code) {
    case LicenseCode.required:
      return join(`${display} requires a license: paste it above.`);
    case LicenseCode.invalid: {
      const check = CHECK.exec(message)?.[1]?.toLowerCase();
      const why = check !== undefined ? CHECK_WORDS[check] : undefined;
      return join(
        why !== undefined
          ? `This license does not fit ${display}: ${why}.`
          : `This license does not fit ${display}${check !== undefined ? `: the ${check} check failed` : ""}.`,
      );
    }
    case LicenseCode.expired:
      return join("This license has expired.");
  }
  return undefined;
}

/** isExpired reports whether an accepted license is past its expiry. */
export function isExpired(l: InstalledLicense | undefined, now: number = Date.now()): boolean {
  if (l === undefined) {
    return false;
  }
  const t = new Date(l.expires).getTime();
  return !Number.isNaN(t) && t <= now;
}

/** licenseWords is an installed solution's license in a few words: *licensed until 1 March 2027*, or *license expired*. */
export function licenseWords(l: InstalledLicense | undefined, now: number = Date.now()): string | undefined {
  if (l === undefined) {
    return undefined;
  }
  return isExpired(l, now) ? "license expired" : `licensed until ${dayOf(l.expires, now)}`;
}

/** termsRows are the builder's terms as given, each a name and its value as text: shown, never read. */
export function termsRows(terms: Record<string, unknown> | undefined): { name: string; value: string }[] {
  return Object.entries(terms ?? {}).map(([name, v]) => ({
    name,
    value:
      typeof v === "string"
        ? v
        : typeof v === "number" || typeof v === "boolean"
          ? String(v)
          : JSON.stringify(v),
  }));
}
